/**
 * SchemeCatalogPage.jsx
 * ──────────────────────
 * Browse ALL IPM schemes without going through the 4-step wizard.
 * Accessible to: risk_manager and admin roles only.
 *
 * Features:
 *   - Filter bar (crop, actor, objective, complexity) — auto-applies on change
 *   - Active filter count badge + clear all
 *   - Displays the full SchemeCard for each result
 *   - Shows user name + role in the page header
 *   - Sign-out link
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchWizardOptions, fetchCatalog } from "../../services/api";
import SchemeCard from "../../components/SchemeCard";

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROLE_LABEL = { admin: "Administrator", risk_manager: "Risk Manager" };

const COMPLEXITY_ORDER = { Low: 1, Medium: 2, High: 3 };

function roleBadgeStyle(role) {
  if (role === "admin")        return { background: "#E6F1FB", color: "#185FA5" };
  if (role === "risk_manager") return { background: "#EDF5E0", color: "#2d6e1f" };
  return { background: "#f1f3f5", color: "#495057" };
}

// ── Styles ────────────────────────────────────────────────────────────────────

const GLOBAL_CSS = `
  :root {
    --green-900:#2e4d0e;--green-800:#3a5c12;--green-700:#3d6213;
    --green-600:#4B7518;--green-500:#5d8f22;--green-400:#7da72f;
    --green-300:#9BC071;--lime-500:#c4ca60;--lime-400:#D4D980;
    --lime-300:#E8EDB4;--cream:#F6F9F2;--cream-2:#edf5e1;
    --brown-900:#3a2800;--brown-700:#5C4B00;--brown-500:#775F46;
  }
  .cat-root,
  .cat-root * { box-sizing: border-box; }
  .cat-root {
    font-family: 'Nunito', system-ui, -apple-system, sans-serif;
    background: var(--cream);
    color: var(--brown-500);
    -webkit-font-smoothing: antialiased;
  }

  /* Nav */
  .cat-nav {
    background: #fff; border-bottom: 2px solid var(--lime-400);
    position: sticky; top: 0; z-index: 100;
    transition: box-shadow .35s;
  }
  .cat-nav.scrolled { box-shadow: 0 4px 24px rgba(75,117,24,.13); }
  .cat-nav-inner {
    max-width: 1400px; margin: 0 auto; padding: 0 28px;
    height: 72px; display: flex; align-items: center; justify-content: space-between;
  }
  .cat-nav-left { display: flex; align-items: center; gap: 24px; }
  .cat-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; }
  .cat-brand img { height: 56px; object-fit: contain; }
  .cat-nav-link {
    font-size: 15px; font-weight: 600; color: var(--brown-500);
    text-decoration: none; padding: 6px 12px; border-radius: 8px;
    transition: color .2s, background .2s;
  }
  .cat-nav-link:hover { color: var(--green-600); background: rgba(75,117,24,.07); }
  .cat-nav-link.active {
    background: linear-gradient(135deg, var(--lime-400), #cdd46e);
    color: #4a5500; font-weight: 700;
  }
  .cat-nav-right { display: flex; align-items: center; gap: 12px; }
  .cat-user-chip {
    display: flex; align-items: center; gap: 8px;
    background: var(--cream-2); border: 1px solid var(--lime-400);
    border-radius: 99px; padding: 5px 12px 5px 8px; font-size: 12px;
  }
  .cat-user-chip .avatar {
    width: 26px; height: 26px; border-radius: 50%;
    background: linear-gradient(135deg, var(--green-600), var(--green-400));
    color: #fff; display: grid; place-items: center; font-size: 11px; font-weight: 700;
  }
  .cat-user-chip .uname { font-weight: 700; color: var(--brown-700); }
  .cat-user-chip .urole { color: var(--green-600); font-weight: 600; }
  .cat-signout {
    font-size: 12px; font-weight: 600; color: var(--brown-500); background: none; border: none;
    cursor: pointer; padding: 6px 10px; border-radius: 8px;
    transition: color .2s, background .2s; font-family: inherit;
    display: flex; align-items: center; gap: 6px;
  }
  .cat-signout:hover { color: #b91c1c; background: #fef2f2; }

  /* Page header */
  .cat-header {
    background: linear-gradient(135deg, var(--green-900) 0%, var(--green-700) 55%, var(--green-600) 100%);
    padding: 40px 28px 48px; position: relative; overflow: hidden;
  }
  .cat-header-deco {
    position: absolute; inset: 0; pointer-events: none; overflow: hidden;
  }
  .cat-header-inner {
    max-width: 1400px; margin: 0 auto; position: relative; z-index: 2;
  }
  .cat-header h1 {
    font-size: clamp(26px, 3vw, 38px); font-weight: 900; color: #fff;
    margin: 0 0 8px; line-height: 1.15; letter-spacing: -.015em;
  }
  .cat-header h1 span { color: var(--lime-400); font-style: italic; }
  .cat-header p {
    font-size: 15px; color: rgba(255,255,255,.82); max-width: 580px;
    line-height: 1.7; margin: 0;
  }
  .cat-header-wave {
    display: block; width: 100%; height: 44px; margin-bottom: -1px; position: relative; z-index: 2;
  }

  /* Filter bar */
  .cat-filter-bar {
    background: #fff; border-bottom: 1.5px solid #e8f0db;
    position: sticky; top: 72px; z-index: 90;
    box-shadow: 0 4px 18px rgba(0,0,0,.04);
  }
  .cat-filter-inner {
    max-width: 1400px; margin: 0 auto; padding: 12px 28px;
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  }
  .cat-filter-label {
    font-size: 11px; font-weight: 800; text-transform: uppercase;
    letter-spacing: .1em; color: var(--green-400); white-space: nowrap;
  }
  .cat-select {
    padding: 8px 32px 8px 12px; border: 1.5px solid #d4dbc8; border-radius: 9px;
    font-size: 13px; font-weight: 600; color: var(--brown-700);
    background: var(--cream) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23775F46' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 10px center;
    appearance: none; font-family: inherit; cursor: pointer;
    transition: border-color .2s, box-shadow .2s;
  }
  .cat-select:focus { outline: none; border-color: var(--green-600); box-shadow: 0 0 0 3px rgba(75,117,24,.12); }
  .cat-clear-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 9px; border: 1.5px solid #f0a0a0;
    background: #fff5f5; color: #b91c1c; font-size: 12px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: background .2s;
  }
  .cat-clear-btn:hover { background: #fee2e2; }
  .cat-filter-count {
    margin-left: auto; font-size: 12px; color: var(--brown-500);
    white-space: nowrap; font-weight: 600;
  }
  .cat-filter-count strong { color: var(--green-600); }

  /* Main content */
  .cat-main { max-width: 1400px; margin: 0 auto; padding: 28px 28px 64px; }

  /* Print */
  @media print {
    .cat-nav,
    .cat-header,
    .cat-header-wave,
    .cat-filter-bar,
    .cat-no-print { display: none !important; }
    .cat-root { background: #fff !important; }
    .cat-main { padding: 8px 0 !important; }
    .cat-group-head { break-inside: avoid; page-break-inside: avoid; }
    .cat-print-header { display: block !important; }
  }
  .cat-print-header {
    display: none;
    margin-bottom: 18px;
    border-bottom: 2px solid #4B7518;
    padding-bottom: 10px;
  }
  .cat-print-header h2 {
    font-size: 20px; font-weight: 900; color: #3d6213;
    margin: 0 0 4px; font-family: 'Nunito', sans-serif;
  }
  .cat-print-header p {
    font-size: 12px; color: #775F46; margin: 0;
  }

  /* Result meta row */
  .cat-meta {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 12px; margin-bottom: 22px;
  }
  .cat-result-count {
    font-size: 14px; font-weight: 600; color: var(--brown-700);
  }
  .cat-result-count span { color: var(--green-600); font-weight: 900; font-size: 17px; }

  /* Scheme group header */
  .cat-group-head {
    display: flex; align-items: center; gap: 10px;
    margin: 32px 0 12px; padding-bottom: 10px;
    border-bottom: 2px solid var(--lime-300);
  }
  .cat-group-head .crop-badge {
    display: inline-flex; align-items: center; gap: 7px;
    background: linear-gradient(135deg, var(--green-700), var(--green-500));
    color: #fff; font-size: 12px; font-weight: 800; letter-spacing: .04em;
    padding: 5px 13px; border-radius: 99px;
    text-transform: uppercase;
  }
  .cat-group-head .complexity-badge {
    font-size: 12px; font-weight: 700; padding: 4px 11px; border-radius: 99px;
  }
  .complexity-low  { background: #f0f8e8; color: var(--green-500); border: 1px solid var(--green-300); }
  .complexity-med  { background: #e8f2d8; color: var(--green-600); border: 1px solid var(--green-600); }
  .complexity-high { background: #d8ecb8; color: #2d5010;          border: 1px solid #2d5010; }

  /* Scheme cards spacing */
  .cat-scheme-list { display: flex; flex-direction: column; gap: 16px; }

  /* States */
  .cat-state {
    text-align: center; padding: 64px 28px;
    color: var(--brown-500); font-size: 15px; line-height: 1.7;
  }
  .cat-state svg { display: block; margin: 0 auto 18px; opacity: .4; }
  .cat-state h3 { font-size: 18px; font-weight: 800; color: var(--brown-700); margin: 0 0 8px; }
  .cat-state .retry-btn {
    display: inline-flex; align-items: center; gap: 8px; margin-top: 16px;
    padding: 9px 20px; border-radius: 10px; border: none;
    background: var(--green-600); color: #fff; font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: background .2s;
  }
  .cat-state .retry-btn:hover { background: var(--green-700); }

  .cat-spinner {
    display: flex; align-items: center; justify-content: center; gap: 10px;
    padding: 60px 28px; color: var(--brown-500); font-size: 14px;
  }
  @keyframes cat-spin { to { transform: rotate(360deg); } }
  .cat-spin-ring {
    width: 24px; height: 24px; border-radius: 50%;
    border: 3px solid var(--lime-400); border-top-color: var(--green-600);
    animation: cat-spin .8s linear infinite;
  }

  /* Footer */
  .cat-footer {
    background: var(--brown-700); color: rgba(255,255,255,.7);
    font-size: 12px; padding: 20px 28px; text-align: center;
  }

  /* ── Hamburger / mobile nav ── */
  .cat-hamburger {
    display: none; background: none; border: none; cursor: pointer;
    padding: 8px; border-radius: 8px; color: var(--brown-500);
    transition: background .2s;
    align-items: center; justify-content: center; flex-shrink: 0;
  }
  .cat-hamburger:hover { background: rgba(75,117,24,.08); }
  .cat-mobile-menu {
    display: none; flex-direction: column; gap: 2px;
    background: #fff; border-top: 1px solid var(--lime-300);
    padding: 10px 16px 16px;
    box-shadow: 0 8px 24px rgba(0,0,0,.08);
  }
  .cat-mobile-menu.open { display: flex; }
  .cat-mobile-menu .cat-nav-link { padding: 10px 14px !important; font-size: 15px; border-radius: 10px; display: block; }
  .cat-mobile-menu .cat-nav-link.active {
    background: linear-gradient(135deg, var(--lime-400), #cdd46e); color: #4a5500; font-weight: 700;
  }
  .cat-mobile-user {
    display: flex; align-items: center; justify-content: space-between;
    border-top: 1px solid var(--lime-300); margin-top: 8px; padding-top: 12px;
  }
  .cat-mobile-user .user-info { display: flex; align-items: center; gap: 8px; }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .cat-hamburger { display: flex !important; }
    .cat-nav-links-desktop { display: none !important; }
    .cat-nav-right { display: none !important; }
    .cat-nav-inner { height: 60px !important; padding: 0 16px !important; }
    .cat-brand img { height: 44px !important; }

    .cat-filter-bar { top: 60px !important; }
    .cat-filter-inner {
      flex-direction: column !important; align-items: stretch !important;
      gap: 8px !important; padding: 10px 16px !important;
    }
    .cat-select { width: 100% !important; }
    .cat-filter-label { margin-bottom: 2px; }
    .cat-filter-count { margin-left: 0 !important; }

    .cat-header { padding: 24px 16px 36px !important; }
    .cat-header h1 { font-size: clamp(22px, 6vw, 28px) !important; }
    .cat-main { padding: 16px 16px 48px !important; }

    .cat-group-head { flex-wrap: wrap; gap: 8px; }
  }
`;


// ── Sub-components ────────────────────────────────────────────────────────────

function CatNavbar({ user, onSignOut, scrolled }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const initials  = user?.name ? user.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";
  const isAdmin   = user?.role === "admin";

  const NavLinks = ({ mobile }) => (
    <>
      <a href="/"        className={`cat-nav-link${mobile ? "" : ""}`}>Home</a>
      <a href="/search"  className="cat-nav-link">Guided Search</a>
      <a href="/info"    className="cat-nav-link">About</a>
      <a href="/catalog" className="cat-nav-link active">Catalog</a>
      {isAdmin && (
        <a href="/admin" className="cat-nav-link" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          Admin panel
        </a>
      )}
    </>
  );

  const UserChip = () => isAdmin ? (
    <a href="/admin" className="cat-user-chip" style={{ textDecoration: "none", cursor: "pointer" }} title="Go to Admin panel">
      <div className="avatar">{initials}</div>
      <div>
        <div className="uname">{user?.name}</div>
        <div className="urole">{ROLE_LABEL[user?.role] ?? user?.role}</div>
      </div>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4, opacity: .55 }} strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
      </svg>
    </a>
  ) : (
    <div className="cat-user-chip">
      <div className="avatar">{initials}</div>
      <div>
        <div className="uname">{user?.name}</div>
        <div className="urole">{ROLE_LABEL[user?.role] ?? user?.role}</div>
      </div>
    </div>
  );

  return (
    <nav className={`cat-nav${scrolled ? " scrolled" : ""}`}>
      <div className="cat-nav-inner">
        <div className="cat-nav-left">
          <a href="/" className="cat-brand">
            <img src="/logo.png" alt="BeXyl" onError={e => e.target.style.display = "none"} />
          </a>
          {/* Desktop nav links */}
          <div className="cat-nav-links-desktop" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <NavLinks />
          </div>
        </div>

        {/* Desktop right: user chip + signout */}
        <div className="cat-nav-right">
          <UserChip />
          <button className="cat-signout" onClick={onSignOut} title="Sign out">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
            Sign out
          </button>
        </div>

        {/* Hamburger button — only on mobile */}
        <button
          className="cat-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      <div className={`cat-mobile-menu${menuOpen ? " open" : ""}`} role="navigation">
        <NavLinks mobile />
        <div className="cat-mobile-user">
          <div className="user-info">
            <div className="avatar" style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "linear-gradient(135deg, var(--green-600), var(--green-400))",
              color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700,
            }}>{initials}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-700)" }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: "var(--green-600)", fontWeight: 600 }}>{ROLE_LABEL[user?.role] ?? user?.role}</div>
            </div>
          </div>
          <button className="cat-signout" onClick={onSignOut}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
            Sign out
          </button>
        </div>
      </div>
    </nav>
  );
}

function PageHeader({ user, total }) {
  return (
    <>
      <div className="cat-header">
        <div className="cat-header-deco" aria-hidden="true">
          <svg style={{ position: "absolute", top: -20, right: -40, opacity: .1 }} width="440" height="340" viewBox="0 0 440 340">
            <circle cx="360" cy="80" r="160" stroke="#D4D980" strokeWidth="1.5" fill="none" />
            <circle cx="360" cy="80" r="100" stroke="#D4D980" strokeWidth="1"   fill="none" />
            <circle cx="360" cy="80" r="52"  stroke="#D4D980" strokeWidth="1"   fill="none" />
          </svg>
        </div>
        <div className="cat-header-inner">
          <div style={{ marginBottom: 12 }}>
            <span style={{ ...roleBadgeStyle(user?.role), fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 99, letterSpacing: ".05em" }}>
              {ROLE_LABEL[user?.role] ?? user?.role}
            </span>
          </div>
          <h1>IPM <span>Scheme Catalog</span></h1>
          <p>
            Browse and filter all available IPM schemes across every actor, crop, objective, and
            management intensity, without stepping through the guided wizard. <br></br>
            {total != null && <> Currently showing <strong style={{ color: "var(--lime-400)" }}>{total} scheme{total !== 1 ? "s" : ""}</strong>.</>}
          </p>
        </div>
      </div>
      <svg className="cat-header-wave" viewBox="0 0 1200 44" preserveAspectRatio="none">
        <path d="M0,14 C200,44 360,2 560,20 C740,38 900,6 1100,18 L1200,22 L1200,44 L0,44 Z" fill="#F6F9F2" />
        <path d="M0,18 C200,46 360,6 560,24 C740,40 900,10 1100,22 L1200,26" fill="none" stroke="#D4D980" strokeWidth="2" opacity=".7" />
      </svg>
    </>
  );
}

function FilterBar({ options, filters, setFilters, activeFilters, onClear, total }) {
  if (!options) return null;

  function set(key, val) {
    setFilters(f => ({ ...f, [key]: val }));
  }

  return (
    <div className="cat-filter-bar">
      <div className="cat-filter-inner">
        <span className="cat-filter-label">Filter by</span>

        <select className="cat-select" value={filters.actor_id} onChange={e => set("actor_id", e.target.value)}>
          <option value="">All actors</option>
          {options.actors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>

		<select className="cat-select" value={filters.crop_id} onChange={e => set("crop_id", e.target.value)}>
          <option value="">All crops</option>
          {options.crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select className="cat-select" value={filters.objective_id} onChange={e => set("objective_id", e.target.value)}>
          <option value="">All objectives</option>
          {options.objectives.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>

        <select className="cat-select" value={filters.complexity} onChange={e => set("complexity", e.target.value)}>
          <option value="">All intensities</option>
          {options.complexity.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {activeFilters > 0 && (
          <button className="cat-clear-btn" onClick={onClear}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Clear ({activeFilters})
          </button>
        )}

        <div className="cat-filter-count">
          {total != null && <><strong>{total}</strong> scheme{total !== 1 ? "s" : ""}</>}
        </div>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="cat-spinner">
      <div className="cat-spin-ring" />
      Loading schemes…
    </div>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <div className="cat-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <h3>Could not load schemes</h3>
      <p>{error}</p>
      <button className="retry-btn" onClick={onRetry}>Try again</button>
    </div>
  );
}

function EmptyState({ activeFilters, onClear }) {
  return (
    <div className="cat-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
      <h3>No schemes match your filters</h3>
      <p>Try adjusting or clearing the active filters.</p>
      {activeFilters > 0 && (
        <button className="cat-clear-btn" style={{ margin: "16px auto 0" }} onClick={onClear}>Clear all filters</button>
      )}
    </div>
  );
}

// Filter pills — shown on screen AND included in print output
const FILTER_DEFS = [
  { key: "crop_id",      label: "Crop",      listKey: "crops" },
  { key: "actor_id",     label: "Actor",     listKey: "actors" },
  { key: "objective_id", label: "Objective", listKey: "objectives" },
  { key: "complexity",   label: "Intensity", listKey: "complexity" },
];

function FilterPills({ filters, options }) {
  if (!options) return null;
  const active = FILTER_DEFS.filter(({ key }) => filters[key]);
  if (!active.length) {
    return (
      <div style={{ marginBottom: 18 }}>
        <span style={{
          fontSize: 12, padding: "4px 14px", borderRadius: 20,
          background: "#f1f3f5", color: "#775F46",
          border: "1px solid #d4dbc8", fontWeight: 600,
        }}>
          All crops · All actors · All objectives · All intensities
        </span>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
      {active.map(({ key, label, listKey }) => {
        const list  = options[listKey] ?? [];
        const match = list.find(o => (o.id ?? o) === filters[key]);
        const name  = match?.name ?? filters[key];
        return (
          <span key={key} style={{
            fontSize: 12, padding: "4px 12px", borderRadius: 20,
            background: "#4B7518", color: "#fff",
            border: "1px solid #3a5c10", fontWeight: 600,
          }}>
            <span style={{ fontWeight: 400, opacity: 0.78 }}>{label}: </span>
            {name}
          </span>
        );
      })}
    </div>
  );
}

function complexityClass(c) {
  return c === "Low" ? "complexity-low" : c === "Medium" ? "complexity-med" : "complexity-high";
}

function Results({ schemes, activeFilters, onClear, options }) {
  if (!schemes.length) return <EmptyState activeFilters={activeFilters} onClear={onClear} />;

  // Group by crop → complexity for a structured view when no filters are active
  const grouped = {};
  for (const s of schemes) {
    const crop = s.crop_id;
    const comp = s.complexity;
    if (!grouped[crop]) grouped[crop] = {};
    if (!grouped[crop][comp]) grouped[crop][comp] = [];
    grouped[crop][comp].push(s);
  }

  const cropName = (id) => {
    const c = options?.crops?.find(c => c.id === id);
    return c ? c.name : id.charAt(0).toUpperCase() + id.slice(1);
  };

  const cropOrder = Object.keys(grouped).sort();
  const complexityOrder = ["Low", "Medium", "High"];

  return (
    <div>
      {cropOrder.map(crop => (
        <div key={crop}>
          {complexityOrder.filter(cx => grouped[crop]?.[cx]?.length).map(cx => (
            <div key={cx}>
              <div className="cat-group-head">
                <span className="crop-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 8C8 10 5.9 16.17 3.82 19.82A1 1 0 0 0 5 21C5 21 7 20 9 20c2 0 4-1 6-4s5-5 5-8-3-3-3 0z"/></svg>
                  {cropName(crop)}
                </span>
                <span className={`complexity-badge ${complexityClass(cx)}`}>{cx} Intensity</span>
                <span style={{ fontSize: 12, color: "var(--brown-500)", fontWeight: 600 }}>
                  {grouped[crop][cx].length} scheme{grouped[crop][cx].length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="cat-scheme-list">
                {grouped[crop][cx].map(scheme => (
                  <SchemeCard key={scheme.scheme_id} scheme={scheme} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SchemeCatalogPage() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();

  const [scrolled,  setScrolled]  = useState(false);
  const [options,   setOptions]   = useState(null);
  const [filters,   setFilters]   = useState({ crop_id: "", actor_id: "", objective_id: "", complexity: "" });
  const [results,   setResults]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");

  // Scroll listener for sticky nav shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Load filter options once
  useEffect(() => {
    fetchWizardOptions().then(setOptions).catch(() => {});
  }, []);

  // Reload schemes whenever filters change
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await fetchCatalog(filters);
      setResults(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  function clearFilters() {
    setFilters({ crop_id: "", actor_id: "", objective_id: "", complexity: "" });
  }

  function handleSignOut() {
    logout();
    navigate("/");
  }

  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <div className="cat-root" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{GLOBAL_CSS}</style>

      <CatNavbar user={user} onSignOut={handleSignOut} scrolled={scrolled} />

      <PageHeader user={user} total={results?.total} />

      <FilterBar
        options={options}
        filters={filters}
        setFilters={setFilters}
        activeFilters={activeFilters}
        onClear={clearFilters}
        total={results?.total}
      />

      <main className="cat-main" style={{ flex: 1 }}>
        {loading && <Loading />}
        {!loading && error && <ErrorState error={error} onRetry={load} />}
        {!loading && !error && results && (
          <>
            {/* Print-only header — hidden on screen, visible when printing */}
            <div className="cat-print-header">
              <h2>BeXyl IPM Scheme Catalog</h2>
              <p>
                Printed on {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                {" · "}{results.total} scheme{results.total !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Filter pills — always visible, included in print */}
            <FilterPills filters={filters} options={options} />

            {/* Print bar — screen only */}
            <div className="cat-no-print" style={{
              background: "#fff", border: "1px solid #d4dbc8", borderRadius: 10,
              padding: "12px 18px", marginBottom: 14,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 10,
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#5C4B00",
                  fontFamily: "'Nunito', sans-serif" }}>
                  <span style={{ color: "var(--green-600)", fontSize: 17, fontWeight: 900 }}>
                    {results.total}
                  </span>
                  {" "}scheme{results.total !== 1 ? "s" : ""} found
                  {/* {results.total > 0 && " — sorted by expert score"} */}
                  {results.total > 0 && ""}
                </div>
                <div style={{ fontSize: 13, color: "#775F46", marginTop: 2 }}>
                  {activeFilters > 0
                    ? `${activeFilters} filter${activeFilters !== 1 ? "s" : ""} active`
                    : "Showing all schemes across all crops and actors"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => window.print()} style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  background: "#5C4B00", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 18px", fontSize: 13,
                  fontWeight: 600, cursor: "pointer", fontFamily: "'Nunito', sans-serif",
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print results
                </button>
                {activeFilters > 0 && (
                  <button onClick={clearFilters} style={{
                    background: "#fff", border: "1px solid #f0a0a0", borderRadius: 8,
                    padding: "8px 14px", fontSize: 13, color: "#b91c1c",
                    cursor: "pointer", fontFamily: "'Nunito', sans-serif", fontWeight: 600,
                  }}>
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            <Results
              schemes={results.schemes}
              activeFilters={activeFilters}
              onClear={clearFilters}
              options={options}
            />
          </>
        )}
      </main>

      <footer className="cat-footer">
        BeXyl WP7 · IPM Decision Support System · EU Horizon Europe Grant No. 101060593
      </footer>
    </div>
  );
}
